import { LoadingIndicator, PageBreadcrumbs, PageHeader } from "@components";
import { nanoid } from "nanoid";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ConfirmModal } from "@/components/application/modals/confirm-modal";
import { SelectedQuestionsList } from "@/components/application/questions";
import QuestionBar from "@/components/ui/questionBar";
import { useToast } from "@/providers/toast-provider";
import { formulario_criar } from "@/services/analytics/events";
import type { Question } from "@/types/question";
import {
	ActiveQuestionComponent,
	AddQuestionModal,
	FormSectionItem,
} from "../../components";
import {
	useCreateChecklistTemplate,
	useUpdateChecklistTemplate,
} from "../../hooks";
import { useChecklistTemplateById } from "../../hooks/useChecklistTemplateQueries";
import { formSchema } from "../../schemas/form.schema";
import { checklistQuestionsService } from "../../services/checklistQuestionsService";
import { useFormBuilderStore } from "../../store/formBuilderStore";
import type {
	ChecklistTemplateDetail,
	CreateTemplatePayload,
	UpdateChecklistPayload,
} from "../../types";
import {
	FORM_DESCRIPTION_MAX_LENGTH,
	FORM_NAME_MAX_LENGTH,
	SECTION_DESCRIPTION_MAX_LENGTH,
	SECTION_TITLE_MAX_LENGTH,
	TemplateStatus,
} from "../../types/template";

type SaveMode = "draft" | "publish";
type ConfirmKind = "save" | "leave" | null;

const getLoadingMessage = (isLoadingTemplate: boolean, isEdit: boolean) => {
	if (!isLoadingTemplate) {
		return "Carregando perguntas...";
	}
	if (isEdit) {
		return "Carregando rascunho...";
	}
	return "Carregando cópia do formulário...";
};

const mapTemplateSectionsToStore = (
	apiSections: NonNullable<ChecklistTemplateDetail["sections"]>
) =>
	[...apiSections]
		.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
		.map((section) => ({
			id: nanoid(),
			title: section.name,
			description: section.description ?? "",
			questions: [...(section.questions ?? [])]
				.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
				.map(
					(q): Question =>
						({
							id: q.questionId,
							identifier: null,
							prompt: q.prompt ?? "",
							description: q.description ?? null,
							isRequired: q.isRequired ?? false,
							type: (q.type ?? 1) as Question["type"],
							questionType: q.type ?? 1,
							settings: {
								optionDisplayType: q.optionDisplayType ?? undefined,
								questionOptions: q.options?.map((op) => ({
									id: op.id,
									label: op.label,
									position: op.position ?? 0,
								})),
								options: q.options,
							},
						}) as Question
				),
		}));

const FormNew = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const duplicateFromId = searchParams.get("duplicateFrom");
	const editFromId = searchParams.get("editFrom");
	const { showToast } = useToast();
	const [openModal, setOpenModal] = useState(false);
	const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
	const [saveMode, setSaveMode] = useState<SaveMode>("publish");
	const [loadingQuestions, setLoadingQuestions] = useState(false);
	const [editingTemplateId, setEditingTemplateId] = useState<number | null>(
		null
	);
	const [allowLeave, setAllowLeave] = useState(false);
	const createTemplate = useCreateChecklistTemplate();
	const updateTemplate = useUpdateChecklistTemplate();
	const hasLoadedSource = useRef(false);
	const hasHandledSourceError = useRef(false);

	const sourceTemplateId = (() => {
		const sourceId = editFromId ?? duplicateFromId;
		if (!sourceId) {
			return null;
		}
		const parsed = Number.parseInt(sourceId, 10);
		if (!Number.isInteger(parsed)) {
			return null;
		}
		return parsed;
	})();

	const {
		data: sourceTemplate,
		isError: isSourceTemplateError,
		isLoading: isLoadingTemplateQuery,
		isFetching: isFetchingTemplate,
	} = useChecklistTemplateById(sourceTemplateId, {
		enabled: sourceTemplateId !== null,
	});

	const isLoadingTemplate =
		sourceTemplateId !== null &&
		!sourceTemplate &&
		(isLoadingTemplateQuery || isFetchingTemplate);

	const {
		formName,
		setFormName,
		formDescription,
		setFormDescription,
		sections,
		selectedQuestionIds,
		setSelectedQuestionIds,
		totalQuestions,
		addSection,
		updateSectionTitle,
		updateSectionDescription,
		duplicateSection,
		deleteSection,
		addQuestionsToLastSection,
		deleteQuestion,
		toggleRequired,
		moveQuestion,
		moveSection,
		getLastSection,
		loadFromTemplate,
		reset,
	} = useFormBuilderStore();

	const [sectionDragId, setSectionDragId] = useState<string | null>(null);
	const [sectionDragOverIndex, setSectionDragOverIndex] = useState<
		number | null
	>(null);
	const [isSectionDragging, setIsSectionDragging] = useState(false);
	const sectionDragOverIndexRef = useRef<number | null>(null);
	const isSectionDraggingRef = useRef(false);

	useEffect(() => {
		reset();
		hasLoadedSource.current = false;
		hasHandledSourceError.current = false;
		setEditingTemplateId(null);
		setAllowLeave(false);
	}, [reset]);

	useEffect(() => {
		if (!(sourceTemplateId && sourceTemplate) || hasLoadedSource.current) {
			return;
		}

		hasLoadedSource.current = true;
		const isEdit = Boolean(editFromId);

		if (isEdit) {
			const isDraft =
				sourceTemplate.status === TemplateStatus.Draft ||
				sourceTemplate.templateStatus === "Draft";

			if (!isDraft) {
				showToast({
					variant: "error",
					title: "Edição indisponível",
					description: "Apenas formulários em rascunho podem ser editados.",
				});
				navigate("/forms/list");
				return;
			}

			setEditingTemplateId(sourceTemplateId);
		}

		const apiSections =
			sourceTemplate.sections ?? sourceTemplate.template?.sections ?? [];
		const mappedSections = mapTemplateSectionsToStore(apiSections);

		loadFromTemplate({
			formName: isEdit
				? (sourceTemplate.name ?? "")
				: `${sourceTemplate.name ?? "Formulário"} (cópia)`,
			formDescription: sourceTemplate.description ?? "",
			sections: mappedSections,
		});
	}, [
		editFromId,
		loadFromTemplate,
		navigate,
		showToast,
		sourceTemplate,
		sourceTemplateId,
	]);

	useEffect(() => {
		if (
			!(sourceTemplateId && isSourceTemplateError) ||
			hasHandledSourceError.current
		) {
			return;
		}

		hasHandledSourceError.current = true;
		showToast({
			variant: "error",
			title: editFromId
				? "Erro ao editar formulário"
				: "Erro ao duplicar formulário",
			description: "Não foi possível carregar o modelo selecionado.",
		});
	}, [editFromId, isSourceTemplateError, showToast, sourceTemplateId]);

	const isSaving = createTemplate.isPending || updateTemplate.isPending;

	const hasUnsavedChanges =
		!allowLeave &&
		(Boolean(formName.trim()) ||
			Boolean(formDescription.trim()) ||
			totalQuestions > 0 ||
			sections.length > 0);

	const handleQuestionAdd = async (selectedIds: number[]) => {
		let lastSection = getLastSection();
		if (!lastSection) {
			addSection();
			lastSection = getLastSection();
		}
		if (!lastSection) {
			return;
		}

		setLoadingQuestions(true);

		try {
			const allQuestionIdsInForm = sections.flatMap((section) =>
				section.questions.map((q) => q.id)
			);

			const newQuestionIds = selectedIds.filter(
				(id) => !allQuestionIdsInForm.includes(id)
			);
			const questionsToAdd = await Promise.all(
				newQuestionIds.map((id) =>
					checklistQuestionsService.getQuestionById(id)
				)
			);

			addQuestionsToLastSection(questionsToAdd);

			const removedIds = allQuestionIdsInForm.filter(
				(id) => !selectedIds.includes(id)
			);
			removedIds.forEach(deleteQuestion);

			setSelectedQuestionIds(selectedIds);
			setOpenModal(false);
		} catch {
			showToast({ variant: "error", title: "Erro ao atualizar perguntas" });
		} finally {
			setLoadingQuestions(false);
		}
	};

	const handleMoveQuestion = (
		questionId: number,
		fromSectionId: string,
		toSectionId: string,
		targetIndex?: number
	) => {
		moveQuestion(questionId, fromSectionId, toSectionId, targetIndex);
	};

	const handleReorder = (questionId: number, direction: "up" | "down") => {
		const lastSection = getLastSection();
		if (!lastSection) {
			return;
		}

		const index = lastSection.questions.findIndex((q) => q.id === questionId);
		if (index === -1) {
			return;
		}

		const newIndex = direction === "up" ? index - 1 : index + 1;
		if (newIndex < 0 || newIndex >= lastSection.questions.length) {
			return;
		}

		moveQuestion(questionId, lastSection.id, lastSection.id, newIndex);
	};

	useEffect(() => {
		if (!sectionDragId) {
			return;
		}

		const handleMouseMove = () => {
			if (!isSectionDraggingRef.current) {
				isSectionDraggingRef.current = true;
				setIsSectionDragging(true);
			}
		};

		const handleMouseUp = () => {
			const overIndex = sectionDragOverIndexRef.current;
			if (isSectionDraggingRef.current && sectionDragId && overIndex !== null) {
				moveSection(sectionDragId, overIndex);
			}
			sectionDragOverIndexRef.current = null;
			isSectionDraggingRef.current = false;
			setSectionDragId(null);
			setSectionDragOverIndex(null);
			setIsSectionDragging(false);
		};

		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [sectionDragId, moveSection]);

	const openConfirmSave = (mode: SaveMode) => {
		if (totalQuestions === 0) {
			showToast({
				variant: "error",
				title: "Nenhuma pergunta adicionada",
				description:
					"Adicione pelo menos uma pergunta antes de salvar o formulário.",
			});
			return;
		}

		const hasUnnamedSection = sections.some(
			(section) => section.title.trim().length === 0
		);
		if (hasUnnamedSection) {
			showToast({
				variant: "error",
				title: "Seção sem nome",
				description:
					"Preencha o nome de todas as seções antes de salvar o formulário.",
			});
			return;
		}

		setSaveMode(mode);
		setConfirmKind("save");
	};

	const buildCreatePayload = async (
		status: (typeof TemplateStatus)[keyof typeof TemplateStatus]
	): Promise<CreateTemplatePayload> => {
		const validated = await formSchema.validate(
			{ formName, formDescription, sections },
			{ context: { sections } }
		);

		const trimmedName = validated.formName?.trim() ?? "";
		const trimmedDescription = formDescription?.trim() ?? "";

		return {
			name: trimmedName,
			description: trimmedDescription || null,
			answerExpirationMinutes: 30,
			template: {
				status,
				sections: sections.map((section, sectionIndex) => ({
					name: section.title.trim(),
					description: section.description?.trim() || null,
					position: sectionIndex + 1,
					questions: section.questions.map((question, questionIndex) => ({
						questionId: question.id,
						position: questionIndex + 1,
						isRequired: question.isRequired ?? false,
					})),
				})),
			},
		};
	};

	const buildUpdatePayload = async (
		status: (typeof TemplateStatus)[keyof typeof TemplateStatus]
	): Promise<UpdateChecklistPayload> => {
		const validated = await formSchema.validate(
			{ formName, formDescription, sections },
			{ context: { sections } }
		);

		const trimmedName = validated.formName?.trim() ?? "";
		const trimmedDescription = formDescription?.trim() ?? "";

		return {
			name: trimmedName,
			description: trimmedDescription || null,
			status,
			sections: sections.map((section, sectionIndex) => ({
				name: section.title.trim(),
				description: section.description?.trim() || null,
				position: sectionIndex + 1,
				questions: section.questions.map((question, questionIndex) => ({
					questionId: question.id,
					position: questionIndex + 1,
					isRequired: question.isRequired ?? false,
				})),
			})),
		};
	};

	const persistForm = async (mode: SaveMode) => {
		if (totalQuestions === 0) {
			showToast({
				variant: "error",
				title: "Nenhuma pergunta adicionada",
				description:
					"Adicione pelo menos uma pergunta antes de salvar o formulário.",
			});
			return false;
		}

		const hasUnnamedSection = sections.some(
			(section) => section.title.trim().length === 0
		);
		if (hasUnnamedSection) {
			showToast({
				variant: "error",
				title: "Seção sem nome",
				description:
					"Preencha o nome de todas as seções antes de salvar o formulário.",
			});
			return false;
		}

		const status =
			mode === "draft" ? TemplateStatus.Draft : TemplateStatus.Active;

		if (editingTemplateId) {
			const payload = await buildUpdatePayload(status);
			await updateTemplate.mutateAsync({
				templateId: editingTemplateId,
				payload,
			});
			return true;
		}

		const formData = await buildCreatePayload(status);
		const response = await createTemplate.mutateAsync(formData);
		formulario_criar(response.checklistId, response.name ?? formData.name);
		return true;
	};

	const leaveToList = () => {
		setAllowLeave(true);
		setConfirmKind(null);
		navigate("/forms/list");
	};

	const handleConfirmSave = async () => {
		try {
			const saved = await persistForm(saveMode);
			if (!saved) {
				return;
			}
			setAllowLeave(true);
			setConfirmKind(null);
			navigate("/forms/list");
		} catch (error) {
			setConfirmKind(null);
			const status = (error as { response?: { status?: number } })?.response
				?.status;
			if (status === 400 || status === 409) {
				return;
			}
			if (error instanceof Error) {
				showToast({
					variant: "error",
					title: "Erro ao salvar formulário",
					description: error.message,
				});
			}
		}
	};

	const handleLeaveSaveDraft = async () => {
		try {
			const saved = await persistForm("draft");
			if (!saved) {
				return;
			}
			leaveToList();
		} catch (error) {
			setConfirmKind(null);
			const status = (error as { response?: { status?: number } })?.response
				?.status;
			if (status === 400 || status === 409) {
				return;
			}
			if (error instanceof Error) {
				showToast({
					variant: "error",
					title: "Erro ao salvar rascunho",
					description: error.message,
				});
			}
		}
	};

	const handleBackNavigation = () => {
		if (hasUnsavedChanges) {
			setConfirmKind("leave");
			return;
		}
		navigate("/forms/list");
	};

	const sectionsForSidebar = sections.map((s) => ({
		id: s.id,
		title: s.title,
		questions: s.questions.map((question) => ({
			id: question.id,
			label: question.prompt,
			type: question.type,
			required: question.isRequired ?? false,
		})),
	}));

	const pageLabel = (() => {
		if (editFromId) {
			return "Editar Rascunho";
		}
		if (duplicateFromId) {
			return "Duplicar Formulário";
		}
		return "Criação de Formulário";
	})();

	const saveConfirmTitle =
		saveMode === "draft" ? "Salvar Rascunho?" : "Publicar formulário?";
	const saveConfirmDescription =
		saveMode === "draft"
			? "Depois de salvar este formulário em rascunho, ele poderá ser editado posteriormente, antes de ser publicado."
			: "Ao confirmar a publicação deste formulário, ele não poderá mais ser editado.";

	return (
		<>
			<AddQuestionModal
				initialSelectedIds={selectedQuestionIds}
				isOpen={openModal}
				onClose={() => setOpenModal(false)}
				onConfirm={handleQuestionAdd}
			/>

			<ConfirmModal
				cancelLabel="Cancelar"
				confirmLabel="Confirmar"
				description={saveConfirmDescription}
				isConfirmLoading={isSaving}
				isOpen={confirmKind === "save"}
				onClose={() => setConfirmKind(null)}
				onConfirm={handleConfirmSave}
				title={saveConfirmTitle}
			/>

			<ConfirmModal
				cancelLabel="Salvar Rascunho"
				confirmLabel="Sair da Página"
				description="Este formulário contém informações que não foram salvas. Se confirmar a saída, elas serão perdidas permanentemente."
				isConfirmLoading={isSaving}
				isOpen={confirmKind === "leave"}
				onCancel={handleLeaveSaveDraft}
				onClose={() => setConfirmKind(null)}
				onConfirm={leaveToList}
				title="Deseja sair da página?"
			/>

			<div className="min-h-screen">
				<div className="min-h-screen p-6">
					<div className="flex flex-col gap-6 lg:flex-row">
						<div className="flex-1 space-y-6 overflow-x-auto">
							<div>
								<PageBreadcrumbs
									ancestors={[
										{
											label: "Formulário",
											onClick: handleBackNavigation,
										},
									]}
									current={pageLabel}
								/>
								<PageHeader title={pageLabel} />
							</div>

							<QuestionBar
								actionButtons={[
									{
										id: "add-question",
										label: "Adicionar pergunta",
										onClick: () => {
											const allQuestionIdsInForm = sections.flatMap((section) =>
												section.questions.map((q) => q.id)
											);
											setSelectedQuestionIds(allQuestionIdsInForm);
											setOpenModal(true);
										},
									},
									{
										id: "divider",
										label: "Adicionar Seção",
										onClick: addSection,
									},
								]}
								draftLabel="Salvar rascunho"
								onSave={() => openConfirmSave("publish")}
								onSaveDraft={() => openConfirmSave("draft")}
								saveDisabled={
									totalQuestions === 0 || isLoadingTemplate || isSaving
								}
								saveLabel="Publicar"
							/>

							{loadingQuestions || isLoadingTemplate ? (
								<output className="flex items-center justify-center rounded-xl border border-border-secondary bg-primary p-12 shadow-xs">
									<LoadingIndicator
										label={getLoadingMessage(
											isLoadingTemplate,
											Boolean(editFromId)
										)}
										size="md"
										type="line-simple"
									/>
								</output>
							) : null}

							<div className="space-y-6">
								<div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
									<div className="mb-6">
										<input
											className="w-full font-bold text-2xl text-gray-900 placeholder:text-gray-400"
											maxLength={FORM_NAME_MAX_LENGTH}
											onChange={(e) => setFormName(e.target.value)}
											placeholder="Nome do Formulário"
											type="text"
											value={formName}
										/>

										<input
											className="w-full text-base text-gray-600 placeholder:text-gray-400"
											maxLength={FORM_DESCRIPTION_MAX_LENGTH}
											onChange={(e) => setFormDescription(e.target.value ?? "")}
											placeholder="Descrição do formulário"
											type="text"
											value={formDescription}
										/>
										<p className="mt-1 text-gray-400 text-xs">
											{formDescription.length}/{FORM_DESCRIPTION_MAX_LENGTH}{" "}
											caracteres
										</p>
									</div>

									<div className="flex flex-col gap-6 space-y-6">
										{sections.map((section, sectionIndex) => (
											// biome-ignore lint/a11y/noNoninteractiveElementInteractions: drop target during section drag
											// biome-ignore lint/a11y/noStaticElementInteractions: drop target during section drag
											<div
												className="relative space-y-4"
												key={section.id}
												onMouseEnter={() => {
													if (
														sectionDragId !== null &&
														sectionDragId !== section.id
													) {
														sectionDragOverIndexRef.current = sectionIndex;
														setSectionDragOverIndex(sectionIndex);
													}
												}}
											>
												{sectionDragOverIndex === sectionIndex &&
												isSectionDragging &&
												sectionDragId !== section.id ? (
													<div className="absolute top-0 right-0 left-0 h-1 rounded bg-blue-500" />
												) : null}
												<FormSectionItem
													description={section.description}
													isActive={false}
													isDragging={
														isSectionDragging === true &&
														sectionDragId === section.id
													}
													onDelete={() => deleteSection(section.id)}
													onDescriptionChange={(description) =>
														updateSectionDescription(
															section.id,
															description.slice(
																0,
																SECTION_DESCRIPTION_MAX_LENGTH
															)
														)
													}
													onDragHandleMouseDown={(event) => {
														if (event.button !== 0) {
															return;
														}
														event.preventDefault();
														isSectionDraggingRef.current = false;
														setSectionDragId(section.id);
														setIsSectionDragging(false);
													}}
													onDuplicate={() => duplicateSection(section.id)}
													onTitleChange={(title) =>
														updateSectionTitle(
															section.id,
															title.slice(0, SECTION_TITLE_MAX_LENGTH)
														)
													}
													title={section.title}
												/>

												<div className="space-y-4">
													{section.questions.map((question) => (
														<ActiveQuestionComponent
															key={question.id}
															onDelete={deleteQuestion}
															onRequiredChange={toggleRequired}
															question={question}
														/>
													))}
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>

						<div className="w-full lg:w-80">
							<div className="sticky top-8">
								<SelectedQuestionsList
									allowReorder={true}
									onMoveQuestion={handleMoveQuestion}
									onReorder={handleReorder}
									sections={sectionsForSidebar}
									title="PERGUNTAS DO FORMULÁRIO"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default FormNew;
